import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { USER_ROLES } from '../../../utils/smartCitiesData';

const OrganizationFields = ({ userRole, formData, onChange, errors }) => {
  const cityOptions = [
    { value: 'atlanta', label: 'Atlanta, GA' },
    { value: 'birmingham', label: 'Birmingham, AL' },
    { value: 'charlotte', label: 'Charlotte, NC' },
    { value: 'jackson', label: 'Jackson, MS' },
    { value: 'memphis', label: 'Memphis, TN' },
    { value: 'new-orleans', label: 'New Orleans, LA' },
    { value: 'richmond', label: 'Richmond, VA' },
    { value: 'other', label: 'Other City' }
  ];

  const universityOptions = [
    { value: 'howard', label: 'Howard University' },
    { value: 'spelman', label: 'Spelman College' },
    { value: 'morehouse', label: 'Morehouse College' },
    { value: 'fisk', label: 'Fisk University' },
    { value: 'tuskegee', label: 'Tuskegee University' },
    { value: 'xavier', label: 'Xavier University of Louisiana' },
    { value: 'clark-atlanta', label: 'Clark Atlanta University' },
    { value: 'north-carolina-at', label: 'North Carolina A&T State University' },
    { value: 'other', label: 'Other' }
  ];

  const departmentOptions = [
    { value: 'urban-planning', label: 'Urban Planning' },
    { value: 'public-works', label: 'Public Works' },
    { value: 'it-department', label: 'IT Department' },
    { value: 'economic-development', label: 'Economic Development' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'sustainability', label: 'Sustainability' },
    { value: 'other', label: 'Other Department' }
  ];

  const majorOptions = [
    { value: 'computer-science', label: 'Computer Science' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'urban-planning', label: 'Urban Planning' },
    { value: 'business', label: 'Business Administration' },
    { value: 'public-policy', label: 'Public Policy' },
    { value: 'data-science', label: 'Data Science' },
    { value: 'design', label: 'Design' },
    { value: 'other', label: 'Other Major' }
  ];

  const yearOptions = [
    { value: 'freshman', label: 'Freshman (1st Year)' },
    { value: 'sophomore', label: 'Sophomore (2nd Year)' },
    { value: 'junior', label: 'Junior (3rd Year)' },
    { value: 'senior', label: 'Senior (4th Year)' },
    { value: 'graduate', label: 'Graduate Student' }
  ];

  // Handle different role types including entrepreneurs and corporate partners
  if (userRole === USER_ROLES?.CITY_OFFICIAL) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Organization Information</h3>
          <p className="text-sm text-muted-foreground">Tell us about your government role</p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="City/Municipality"
              placeholder="Select your city"
              options={cityOptions}
              value={formData?.city}
              onChange={(value) => onChange('city', value)}
              error={errors?.city}
              required
            />
            
            <Input
              label="Organization Name"
              type="text"
              placeholder="e.g., City of Atlanta"
              value={formData?.organization}
              onChange={(e) => onChange('organization', e?.target?.value)}
              error={errors?.organization}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Department"
              placeholder="Select your department"
              options={departmentOptions}
              value={formData?.department}
              onChange={(value) => onChange('department', value)}
              error={errors?.department}
              required
            />
            
            <Input
              label="Job Title"
              type="text"
              placeholder="e.g., Urban Planning Director"
              value={formData?.title}
              onChange={(e) => onChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData?.phone}
              onChange={(e) => onChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />

            <Input
              label="Location"
              type="text"
              placeholder="e.g., Atlanta, GA"
              value={formData?.location}
              onChange={(e) => onChange('location', e?.target?.value)}
              error={errors?.location}
              required
            />
          </div>
        </div>
      </div>
    );
  }

  if (userRole === USER_ROLES?.STUDENT) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Academic Information</h3>
          <p className="text-sm text-muted-foreground">Tell us about your academic background</p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Select
                label="University"
                placeholder="Select your university"
                options={universityOptions}
                value={formData?.university}
                onChange={(value) => onChange('university', value)}
                error={errors?.university}
                required
              />
              {formData?.university === 'other' && (
                <div className="mt-3">
                  <Input
                    label="Enter your university name"
                    type="text"
                    placeholder="Type your university name"
                    value={formData?.customUniversity || ''}
                    onChange={(e) => onChange('customUniversity', e?.target?.value)}
                    error={errors?.customUniversity}
                    required
                  />
                </div>
              )}
            </div>
            
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Major/Field of Study"
              placeholder="Select your major"
              options={majorOptions}
              value={formData?.major}
              onChange={(value) => onChange('major', value)}
              error={errors?.major}
              required
            />
            
            <Select
              label="Academic Year"
              placeholder="Select your year"
              options={yearOptions}
              value={formData?.academicYear}
              onChange={(value) => onChange('academicYear', value)}
              error={errors?.academicYear}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Expected Graduation"
              type="date"
              value={formData?.expectedGraduation}
              onChange={(e) => onChange('expectedGraduation', e?.target?.value)}
              error={errors?.expectedGraduation}
              required
            />
            
            <Input
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData?.phone}
              onChange={(e) => onChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />
          </div>

          <Input
            label="Location"
            type="text"
            placeholder="e.g., Atlanta, GA"
            value={formData?.location}
            onChange={(e) => onChange('location', e?.target?.value)}
            error={errors?.location}
            required
          />
        </div>
      </div>
    );
  }

  if (userRole === USER_ROLES?.PCF_ADMIN) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">Administrative Information</h3>
          <p className="text-sm text-muted-foreground">Verify your administrator credentials</p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">  
            <Input
              label="Department"
              type="text"
              placeholder="e.g., Program Management"
              value={formData?.organization}
              onChange={(e) => onChange('organization', e?.target?.value)}
              error={errors?.organization}
              required
            />
            
            <Input
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              value={formData?.phone}
              onChange={(e) => onChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Job Title"
              type="text"
              placeholder="e.g., Program Administrator"
              value={formData?.title}
              onChange={(e) => onChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            <Input
              label="Location"
              type="text"
              placeholder="e.g., Atlanta, GA"
              value={formData?.location}
              onChange={(e) => onChange('location', e?.target?.value)}
              error={errors?.location}
              required
            />
          </div>

        </div>
      </div>
    );
  }

  // Default form for entrepreneurs, corporate partners, and community members
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Organization Information</h3>
        <p className="text-sm text-muted-foreground">Tell us about your professional background</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Organization/Company"
            type="text"
            placeholder="Enter your organization name"
            value={formData?.organization}
            onChange={(e) => onChange('organization', e?.target?.value)}
            error={errors?.organization}
            required
          />
          
          <Input
            label="Job Title/Role"
            type="text"
            placeholder="e.g., CEO, Director, Manager"
            value={formData?.title}
            onChange={(e) => onChange('title', e?.target?.value)}
            error={errors?.title}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Phone Number"
            type="tel"
            placeholder="(555) 123-4567"
            value={formData?.phone}
            onChange={(e) => onChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />

          <Input
            label="Location"
            type="text"
            placeholder="e.g., Atlanta, GA"
            value={formData?.location}
            onChange={(e) => onChange('location', e?.target?.value)}
            error={errors?.location}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizationFields;